import {Object2DFacade} from 'troika-2d'
import {color} from 'd3-color'
import {
  GRAPH_STROKE_STYLES,
  GRAPH_FILL_STYLES,
  D3_CURVE_INTERPOLATORS,
  CURVE_OPTIONS
} from '../../constants'
import PathDrawingContext from '../../utils/PathDrawingContext'

const HIT_TEST_STROKE_WIDTH = 20

function initLinearGradient(spec, ctx) {
  const grad = ctx.createLinearGradient(spec.x1 || 0, spec.y1 || 0, spec.x2 || 0, spec.y2 || 0)
  spec.stops.forEach(stop => {
    grad.addColorStop(stop.stop, stop.color)
  })
  return grad
}

function transparentize(c, opacity = 0) {
  c = color(c).rgb()
  c.opacity = opacity
  return c.toString()
}


class Path2D extends Object2DFacade {
  /**
   * @property values
   * @property xScale
   * @property yScale
   * @property xRange
   * @property yRange
   * @property fillStyle
   * @property fillOpacity
   * @property strokeStyle
   * @property strokeWidth
   * @property strokeOpacity
   * @property strokeDasharray
   * @property strokeDashoffset
   * @property pathInterpolate
   * @property minY
   * @property maxY
   */

  drawStroke(
    points,
    ctx,
    d3CurveInterpolator,
    strokeStyle,
    strokeWidth,
    strokeColor,
    strokeOpacity,
    strokeDasharray,
    strokeDashoffset,
  ) {
    let ctxWithLength = null
    let curve = d3CurveInterpolator(ctx)

    if (strokeStyle === GRAPH_STROKE_STYLES.ANIMATED) {
      ctxWithLength = new PathDrawingContext(ctx)
      curve = d3CurveInterpolator(ctxWithLength)
    }

    ctx.lineWidth = strokeWidth
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // FIXME stroke gradient support
    // if (typeof strokeStyle === 'object') {
    //   strokeStyle = initLinearGradient(strokeStyle, ctx)
    // }
    ctx.strokeStyle = transparentize(strokeColor, strokeOpacity)
    ctx.beginPath()

    curve.lineStart()
    for (let k = 0, klen = points.length; k < klen; k += 3) {
      curve.point(
        points[k], // x
        points[k + 1] // y1
      )
    }
    curve.lineEnd()

    if (strokeStyle === GRAPH_STROKE_STYLES.DASHED) {
      ctx.setLineDash(strokeDasharray.split(' '))
      ctx.lineDashOffset = strokeDashoffset
    }
    // Sanity check that we've wrapped the context with a getTotalLength implementation
    else if (strokeStyle === GRAPH_STROKE_STYLES.ANIMATED && ctxWithLength) {
      const curveLength = ctxWithLength.getTotalLength()
      ctx.setLineDash([
        curveLength,
        curveLength
      ])
      ctx.lineDashOffset = 2 * curveLength * (1.0 - this.strokeAnimationT) //this.dashOffset || 0.0
    }

    ctx.stroke()

    // Reset
    ctx.setLineDash([])
    ctx.lineDashOffset = 0.0
  }

  drawFill(
    points,
    ctx,
    d3CurveInterpolator,
    fillStyle,
    fillColor,
    fillOpacity,
    gradientY1,
    gradientY2
  ) {
    const curve = d3CurveInterpolator(ctx)

    if (fillStyle === GRAPH_FILL_STYLES.GRADIENT) {
      ctx.fillStyle = initLinearGradient({
        y1: gradientY1,
        y2: gradientY2,
        stops: [
          { stop: 0.0, color: transparentize(fillColor)},
          { stop: 1.0, color: transparentize(fillColor, fillOpacity) }
        ]
      }, ctx)
    }
    else {
      ctx.fillStyle = transparentize(fillColor, fillOpacity)
    }
    ctx.beginPath()

    // Draw top fill path
    curve.lineStart()
    for (let i = 0, len = points.length; i < len; i += 3) {
      curve.point(
        points[i], // x
        points[i + 1] // y1
      )
    }
    curve.lineEnd()

    ctx.lineTo(
      points[points.length - 3],
      points[points.length - 1]
    )

    // Reverse-draw y0 baseline (bottom of fill path)
    curve.lineStart()
    for (let j = 0, jlen = points.length; j < jlen; j += 3) {
      const jj = jlen - j
      curve.point(
        points[jj], // x
        points[jj + 2] // y0
      )
    }
    curve.lineEnd()

    ctx.closePath()
    ctx.fill()
  }

  render(ctx) {
    const {
      values,
      xScale,
      yScale,
      xRange,
      yRange,
      fillStyle,
      fillOpacity = 0.5,
      strokeStyle,
      strokeOpacity = 0.8,
      strokeDasharray = '10 10',
      strokeDashoffset = 0.0,
      pathInterpolate = CURVE_OPTIONS.LINEAR,
      minY,
      maxY,
      strokeWidth = 1,
      fillColor,
      strokeColor,
    } = this

    if (values) {
      // Build x/y points from current data + time window
      const points = []

      for (const timestamp in values) {
        if (values.hasOwnProperty(timestamp)) {
          const {
            y0,
            y1,
            // value,
            // colorValue
          } = values[timestamp]

          points.push(
            xScale(timestamp),
            yScale(y1),
            yScale(y0),
            // colorScale ? colorScale(colorValue) : null
          )
        }
      }

      // Init path interpolator
      const d3CurveInterpolator = D3_CURVE_INTERPOLATORS[pathInterpolate]

      // Clip rect
      ctx.beginPath()
      ctx.rect(xRange[0], yRange[1], xRange[1], yRange[0])
      ctx.clip()

      // Render fill
      if (fillColor) {
        this.drawFill(
          points,
          ctx,
          d3CurveInterpolator,
          fillStyle,
          fillColor,
          fillOpacity,
          yScale(minY),
          yScale(maxY)
        )
      }

      // Render stroke
      if (strokeColor) {
        if (strokeStyle === GRAPH_STROKE_STYLES.ANIMATED) {
          // Draw faded-out constant stroke to be animated "over"
          this.drawStroke(
            points,
            ctx,
            d3CurveInterpolator,
            GRAPH_STROKE_STYLES.SOLID,
            strokeWidth / 2,
            strokeColor,
            0.2,
            strokeDasharray,
            strokeDashoffset
          )
        }

        // Draw main stroke
        this.drawStroke(
          points,
          ctx,
          d3CurveInterpolator,
          strokeStyle,
          strokeWidth,
          strokeColor,
          strokeOpacity,
          strokeDasharray,
          strokeDashoffset
        )

        // Draw invisible thicker stroke to improve hittest accuracy
        this.drawStroke(
          points,
          ctx,
          d3CurveInterpolator,
          GRAPH_STROKE_STYLES.SOLID,
          HIT_TEST_STROKE_WIDTH,
          strokeColor,
          0.0 // Invisible
        )
      }
    }
  }
}

export default Path2D